<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { ref, computed, watch } from "vue"
import { useI18n } from "vue-i18n"
import { useEventBus } from "@vueuse/core"
import { useAuthStore } from "@core/stores/auth.store"
import { CompanyService } from "@core/services"
import { useFetchWrapper } from "@core/composables"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            title: "Modifier le logo de l'entreprise",
            change: "Changer",
            cancel: "Annuler",
            no_image: "Aucune image",
        },
    },
})

// ================================
// TYPES & INTERFACES
// ================================
type Props = {
    /**
     * Indique si la modal est ouverte
     */
    open: boolean
    /**
     * Image recadrée actuelle
     */
    cropImage?: string
    /**
     * Image brute actuelle
     */
    rawImage?: string
    /**
     * Identifiant de l'entreprise
     */
    companyId?: string
}

type Emits = {
    /**
     * Émis quand la modal doit être fermée
     */
    "update:open": [value: boolean]
}

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(defineProps<Props>(), {
    open: false,
    cropImage: "",
    rawImage: "",
    companyId: "",
})

const emit = defineEmits<Emits>()

// ================================
// SERVICES & STORES
// ================================
const authStore = useAuthStore()
const companyService = new CompanyService()

/**
 * Bus d'événements pour les notifications
 */
const busNotification = useEventBus<{
    severity: "warn" | "error" | "success" | "info"
    title: string
    message: string
    life?: number
}>("notification")

// ================================
// STATE
// ================================
/**
 * État de la modal : 'idle' | 'upload' | 'edit' | 'loading'
 */
const state = ref<"idle" | "upload" | "edit" | "loading">("idle")

/**
 * Fichier brut sélectionné
 */
const rawFile = ref<File | null>(null)

/**
 * Image en base64 du fichier sélectionné
 */
const imageBase64 = ref<string | null>(null)

/**
 * Fichier de l'image recadrée
 */
const cropFile = ref<File | null>(null)

/**
 * Message d'erreur
 */
const error = ref<string | null>(null)

// ================================
// COMPUTED
// ================================
/**
 * Indique si la modal est visible
 */
const visible = computed({
    get: () => props.open,
    set: (value: boolean) => emit("update:open", value),
})

/**
 * Détermine l'état initial selon la présence d'une image
 */
const initialState = computed<"idle" | "upload">(() => {
    return props.cropImage ? "idle" : "upload"
})

// ================================
// INTERACTIONS
// ================================
/**
 * Initialise la modal à l'ouverture
 */
function onShow(): void {
    state.value = initialState.value
    resetContext()
}

/**
 * Réinitialise le contexte
 */
function resetContext(): void {
    rawFile.value = null
    imageBase64.value = null
    cropFile.value = null
    error.value = null
}

/**
 * Gère l'upload d'un fichier
 */
function onUploadFile(file: File): void {
    rawFile.value = file
    error.value = null

    // Convertit le fichier en base64
    const reader = new FileReader()
    reader.onload = (e) => {
        imageBase64.value = e.target?.result as string
        state.value = "edit"
    }
    reader.onerror = () => {
        error.value = "Erreur lors de la lecture du fichier"
        state.value = "upload"
    }
    reader.readAsDataURL(file)
}

/**
 * Gère la soumission de l'image éditée
 */
function onSubmitEdit(data: {
    file: File
    cropImage: HTMLCanvasElement
}): void {
    cropFile.value = data.file
    uploadImage()
}

/**
 * Upload l'image vers le serveur
 */
const { fetch: uploadImage, isFetching: isUploading } = useFetchWrapper(
    async () => {
        if (!rawFile.value || !cropFile.value) {
            throw new Error("Fichiers manquants")
        }

        state.value = "loading"
        error.value = null

        try {
            await companyService.uploadCompanyLogo({
                rawFile: rawFile.value,
                cropFile: cropFile.value,
            })

            // Rafraîchit les données utilisateur de manière asynchrone (non bloquant)
            // Ne fait pas échouer le processus si le backend n'est pas disponible
            authStore.fetchUser().catch(() => {
                // Erreur silencieuse : le rafraîchissement a échoué mais l'upload a réussi
            })

            // Affiche une notification de succès
            busNotification.emit({
                severity: "success",
                title: "Succès",
                message: "Le logo a été mis à jour avec succès",
            })

            // Ferme la modal
            visible.value = false
            resetContext()
            state.value = initialState.value
        } catch (err: unknown) {
            const errorMessage =
                err instanceof Error
                    ? err.message
                    : "Une erreur est survenue lors de l'upload du logo"
            error.value = errorMessage
            state.value = "edit"

            busNotification.emit({
                severity: "error",
                title: "Erreur",
                message: errorMessage,
            })
        }
    }
)

/**
 * Gère l'annulation
 */
function onCancel(): void {
    if (state.value === "edit" && !props.cropImage) {
        // Si on est en mode édition et qu'il n'y a pas d'image existante, retour à l'upload
        state.value = "upload"
        resetContext()
    } else {
        // Sinon, ferme la modal
        visible.value = false
        resetContext()
        state.value = initialState.value
    }
}

/**
 * Passe en mode upload
 */
function onGoToUpload(): void {
    state.value = "upload"
    resetContext()
}

// ================================
// WATCHERS
// ================================
watch(visible, (newVisible) => {
    if (newVisible) {
        onShow()
    }
})
</script>

<template>
    <BaseDialog
        v-model:visible="visible"
        modal
        :closable="true"
        dismissable-mask
        position="top"
        class="modal-company-image"
        :style="{ maxWidth: '32rem', width: '100%' }"
        :breakpoints="{ '575px': '90vw' }"
    >
        <template #header>
            <h2 class="text-lg font-bold text-primary">
                {{ t("title") }}
            </h2>
        </template>

        <div class="min-h-[20rem] flex flex-col gap-4">
            <!-- État : Upload -->
            <div v-if="state === 'upload'" class="py-4">
                <BaseInputUpload
                    label="Télécharger une image"
                    accept="image/png, image/jpeg"
                    hint="Le format accepté est PNG, JPEG (max 5MB)"
                    @upload="onUploadFile"
                />
            </div>

            <!-- État : Edit -->
            <div v-else-if="state === 'edit'" class="w-full">
                <BaseImageEdit
                    :image="imageBase64 || ''"
                    :loading="isUploading"
                    @click:cancel="onCancel"
                    @click:submit="onSubmitEdit"
                />
            </div>

            <!-- État : Loading -->
            <div
                v-else-if="state === 'loading'"
                class="modal-company-image__loading flex flex-col items-center justify-center gap-4 py-8"
            >
                <BaseProgressBar mode="indeterminate" class="w-full max-w-xs" />
                <p class="text-sm text-gray-600">Upload en cours...</p>
            </div>

            <!-- État : Idle (affichage de l'image actuelle) -->
            <div v-else class="flex flex-col items-center gap-4">
                <div
                    class="w-48 h-48 rounded-full overflow-hidden flex items-center justify-center bg-gray-100 border-2 border-gray-200"
                >
                    <img
                        v-if="cropImage"
                        :src="cropImage"
                        class="w-full h-full object-cover"
                        alt="Logo actuel"
                    />
                    <div v-else class="text-gray-400 text-sm text-center p-4">
                        {{ t("no_image") }}
                    </div>
                </div>

                <div class="w-full flex justify-center gap-2">
                    <BaseButton
                        :label="t('change')"
                        severity="secondary"
                        @click="onGoToUpload"
                    />
                    <BaseButton
                        :label="t('cancel')"
                        severity="secondary"
                        text
                        @click="onCancel"
                    />
                </div>
            </div>

            <!-- Message d'erreur -->
            <BaseMessage
                v-if="error"
                severity="error"
                variant="simple"
                class="mt-2"
            >
                {{ error }}
            </BaseMessage>
        </div>
    </BaseDialog>
</template>
